import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  productTypeOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportProductConversionData,
} from '@src/types/reports';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportProductConversion,
  getProductConversion,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.dataKey.includes('conversion_percentage')
                ? `${entry.value.toFixed(1)}%`
                : entry.dataKey.includes('revenue')
                  ? `$${entry.value.toLocaleString()}`
                  : entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const ProductConversion: FC = () => {
  // Data state
  const [data, setData] = useState<TReportProductConversionData[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableProductComparing: true,
  });

  // Fixed display items for this report
  const displayItems = ['conversion_percentage', 'revenue'];

  const { columns, tableData } = useReportTableColumns({
    data,
    selectedItems: displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage;
        case 'revenue':
          return item.revenue;
        default:
          return 0;
      }
    },
    getPreviousPeriodField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage_previous;
        case 'revenue':
          return item.revenue_previous;
        default:
          return undefined;
      }
    },
    getPreviousYearField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage_previous_year;
        case 'revenue':
          return item.revenue_previous_year;
        default:
          return undefined;
      }
    },
    valueFormatter: (key, value) => {
      if (key === 'conversion_percentage') {
        return `${value.toFixed(1)}%`;
      }
      if (key === 'revenue') {
        return `$${value.toLocaleString()}`;
      }

      return value.toLocaleString();
    },
    includeTotalsRow: false,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage;
        case 'revenue':
          return item.revenue;
        default:
          return 0;
      }
    },
    getPreviousPeriodField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage_previous;
        case 'revenue':
          return item.revenue_previous;
        default:
          return undefined;
      }
    },
    getPreviousYearField: (item, key) => {
      switch (key) {
        case 'conversion_percentage':
          return item.conversion_percentage_previous_year;
        case 'revenue':
          return item.revenue_previous_year;
        default:
          return undefined;
      }
    },
  });

  const parseSelectedProductId = (value: string): number | null => {
    const trimmed = value?.trim();
    if (!trimmed) {
      return null;
    }

    const productIdMatch = trimmed.match(/^\((\d+)\)/);
    if (productIdMatch) {
      return Number(productIdMatch[1]);
    }

    const numericValue = Number(trimmed);
    return Number.isNaN(numericValue) ? null : numericValue;
  };

  const buildPayload = () => {
    const productAId = parseSelectedProductId(filters.selectedProductA);
    const productBId = parseSelectedProductId(filters.selectedProductB);

    if (!productAId || !productBId) {
      throw new Error('Please select both Product A and Product B');
    }

    return {
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : null,
      end_date: filters.endDate ? dayjs(filters.endDate).format('YYYY-MM-DD') : null,
      grouping: (filters.selectedGroupBy || ReportGroupBy.DAY) as ReportGroupBy,
      product_a: productAId,
      product_b: productBId,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getProductConversion(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildPayload();
      const response = await exportProductConversion(payload, 'csv');

      downloadFile(response.data, 'product-conversion-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Product Conversion - Percentage of Users who Purchase product B after
        Product A
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          enableProductComparing={true}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={productTypeOptions}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProductA={filters.selectedProductA}
          selectedProductB={filters.selectedProductB}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductAChange={filters.setSelectedProductA}
          onProductBChange={filters.setSelectedProductB}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend
                iconType="rect"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data bar */}
              <Bar
                dataKey="conversion_percentage"
                fill={getChartColor(0)}
                name="Conversion (%)"
              />

              {/* Previous period bar */}
              {filters.compareToPreviousPeriod && (
                <Bar
                  dataKey="conversion_percentage_previous_period"
                  fill={getChartColor(0)}
                  fillOpacity={0.5}
                  name="Conversion (%) (Previous Period)"
                />
              )}

              {/* Previous year bar */}
              {filters.compareToSamePeriodPreviousYear && (
                <Bar
                  dataKey="conversion_percentage_previous_year"
                  fill={getChartColor(0)}
                  fillOpacity={0.3}
                  name="Conversion (%) (Previous Year)"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
